import React from 'react';
import { BarChart3, X } from 'lucide-react';
import type { AnalyticsDimensionResult } from '../../api/types';
import type {
  AdvancedRowData,
  AdvancedRowDefinition,
  AggregatedRow,
  DrillStep,
  MetricOption,
  Option,
  TrendDisplayUnit,
  TrendWeightUnit,
} from './types';
import {
  adaptDimensionFromBackend,
  addCopMetricColumn,
  aggregateByDimension,
  convertWeight,
  getCurrencyUnitOptions,
  getTrendUnitCategory,
  getUnitOptionsByCategory,
  isRowSelected,
} from './utils';
import { ChartTypeSelector } from './chart-type-selector';
import { AdvancedDataTable } from './data-table';
import { RowChart } from './row-chart';
import { DetailPanel } from './detail-panel';

type AdvancedDynamicRowProps = {
  definition: AdvancedRowDefinition;
  records: Array<Record<string, unknown>>;
  dimensions: Option[];
  metricOptions: MetricOption[];
  exchangeRateAccessor?: string;
  precomputedBackendDimension?: AnalyticsDimensionResult;
  onRemove: (id: string) => void;
  onChange: (id: string, patch: Partial<AdvancedRowDefinition>) => void;
  drillPath: DrillStep[];
  isPanelActive?: boolean;
  onSelectRow: (payload: {
    dimensionAccessor: string;
    rowKey: string;
    label: string;
    secondaryLabel?: string;
  }) => void;
  onClearDimensionSelection: (dimensionAccessor: string) => void;
  chartRef?: React.RefObject<HTMLDivElement | null>;
};

export function AdvancedDynamicRow({
  definition,
  records,
  dimensions,
  metricOptions,
  exchangeRateAccessor,
  precomputedBackendDimension,
  onRemove,
  onChange,
  drillPath,
  isPanelActive,
  onSelectRow,
  onClearDimensionSelection,
  chartRef,
}: AdvancedDynamicRowProps) {
  const [highlightedKey, setHighlightedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    setHighlightedKey(null);
  }, [definition.dimensionAccessor, definition.metricKey, definition.topN, records]);

  const metric = metricOptions.find(item => item.key === definition.metricKey);
  const dimension = dimensions.find(item => item.key === definition.dimensionAccessor);
  const metricCategory = getTrendUnitCategory(metric?.label ?? definition.metricKey);
  const unitOptions =
    metricCategory === 'currency'
      ? getCurrencyUnitOptions(Boolean(exchangeRateAccessor))
      : getUnitOptionsByCategory(metricCategory);
  const displayUnit = (
    unitOptions.includes(definition.unit as TrendDisplayUnit) ? definition.unit : unitOptions[0]
  ) as TrendDisplayUnit;

  const aggregationInput = React.useMemo(() => {
    if (metricCategory !== 'currency' || displayUnit !== 'COP' || !exchangeRateAccessor) {
      return { records, metricKey: definition.metricKey };
    }
    return addCopMetricColumn(records, definition.metricKey, exchangeRateAccessor);
  }, [records, definition.metricKey, metricCategory, displayUnit, exchangeRateAccessor]);

  const data = React.useMemo((): AdvancedRowData => {
    if (precomputedBackendDimension) {
      return adaptDimensionFromBackend(
        precomputedBackendDimension,
        records,
        definition.dimensionAccessor
      );
    }
    return aggregateByDimension(
      aggregationInput.records,
      definition.dimensionAccessor,
      aggregationInput.metricKey,
      definition.topN
    );
  }, [
    precomputedBackendDimension,
    records,
    aggregationInput.records,
    aggregationInput.metricKey,
    definition.dimensionAccessor,
    definition.topN,
  ]);

  const convertedData = React.useMemo(() => {
    const convertMetricValue = (value: number) => {
      if (metricCategory === 'weight') {
        return convertWeight(value, 'KG', displayUnit as TrendWeightUnit);
      }
      return value;
    };

    return {
      ...data,
      rows: data.rows.map(row => ({
        ...row,
        metricValue: convertMetricValue(row.metricValue),
      })),
      totalMetric: convertMetricValue(data.totalMetric),
      othersMetric: convertMetricValue(data.othersMetric),
    };
  }, [data, metricCategory, displayUnit]);

  const isSelected = React.useCallback(
    (row: AggregatedRow) => isRowSelected(drillPath, row.key, definition.dimensionAccessor),
    [drillPath, definition.dimensionAccessor]
  );

  const selectedDetailRow = React.useMemo(() => {
    if (highlightedKey) {
      return convertedData.rows.find(row => row.key === highlightedKey) ?? null;
    }

    return convertedData.rows.find(row => isSelected(row)) ?? null;
  }, [convertedData.rows, highlightedKey, isSelected]);

  const handleSelect = React.useCallback(
    (row: AggregatedRow) => {
      setHighlightedKey(prev => (prev === row.key ? null : row.key));
      onSelectRow({
        dimensionAccessor: definition.dimensionAccessor,
        rowKey: row.key,
        label: row.label,
        secondaryLabel: row.secondaryLabel,
      });
    },
    [definition.dimensionAccessor, onSelectRow]
  );

  return (
    <article className="overflow-hidden border border-gray-200 bg-white sm:rounded-xl">
      <div className="border-b border-gray-100 px-3 py-2 sm:px-4">
        <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <BarChart3 className="h-4 w-4 shrink-0 text-primary" />
            <h4 className="truncate text-sm font-semibold uppercase tracking-wide text-gray-900">
              {dimension?.label ?? definition.dimensionAccessor}
            </h4>
            <label className="flex shrink-0 items-center gap-2 text-xs text-gray-600">
              <span className="font-medium">Items mostrados:</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  onClick={() =>
                    onChange(definition.id, { topN: Math.max(3, definition.topN - 1) })
                  }
                  disabled={definition.topN <= 3}
                  aria-label="Reducir items"
                >
                  −
                </button>
                <input
                  type="number"
                  min={3}
                  max={50}
                  value={definition.topN}
                  onChange={event =>
                    onChange(definition.id, {
                      topN: Math.min(
                        50,
                        Math.max(3, Number.parseInt(event.target.value, 10) || 10)
                      ),
                    })
                  }
                  className="w-12 rounded-md border border-gray-300 bg-white px-1 py-1 text-center text-xs font-medium text-gray-700"
                />
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  onClick={() =>
                    onChange(definition.id, { topN: Math.min(50, definition.topN + 1) })
                  }
                  disabled={definition.topN >= 50}
                  aria-label="Aumentar items"
                >
                  +
                </button>
              </div>
            </label>
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ChartTypeSelector
              value={definition.chartType}
              onChange={variant => onChange(definition.id, { chartType: variant })}
            />

            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800"
              onClick={() => onRemove(definition.id)}
              title="Eliminar gráfico"
              aria-label="Eliminar gráfico"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        className={`grid gap-4 px-1 py-2 sm:px-4 ${
          selectedDetailRow ? 'xl:grid-cols-[1fr_1fr_220px]' : 'xl:grid-cols-2'
        }`}
      >
        <div className="min-w-0">
          <AdvancedDataTable
            rows={convertedData.rows}
            dimensionLabel={dimension?.label ?? 'Dimension'}
            metricLabel={metric?.label ?? 'Valor'}
            onRowClick={handleSelect}
            isRowSelected={isSelected}
          />
        </div>

        <div className="min-w-0">
          <RowChart
            rows={convertedData.rows}
            variant={definition.chartType}
            metricLabel={metric?.label ?? 'Valor'}
            unit={displayUnit}
            onSelectRow={handleSelect}
            isRowSelected={isSelected}
            isPanelActive={isPanelActive}
            chartRef={chartRef}
          />
        </div>

        {selectedDetailRow ? (
          <div className="min-w-0">
            <DetailPanel
              item={selectedDetailRow}
              totalMetric={convertedData.totalMetric}
              metricLabel={metric?.label ?? 'Valor'}
              unit={displayUnit}
              onClose={() => {
                setHighlightedKey(null);
                onClearDimensionSelection(definition.dimensionAccessor);
              }}
            />
          </div>
        ) : null}
      </div>
    </article>
  );
}
